"use client";

import { ReactNode, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUnlock } from "@fortawesome/pro-regular-svg-icons";
import { Button, buttonVariants } from "~/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { useShopConfig } from "~/hooks/useShopConfig";
import { useTranslations } from "~/hooks/useTranslations";
import { UnlockCodeContent } from "~/app/tickets/[eventId]/_components/UnlockCodeButton/UnlockCodeContent";
import { useMediaQuery } from "usehooks-ts";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "~/components/ui/drawer";
import { Close } from "@radix-ui/react-popover";
import { X } from "lucide-react";
import { cn } from "~/lib/utils";
import cookie from "cookiejs";
import { UNLOCK_CODE_COOKIE } from "~/lib/definitions";

interface IUnlockCodeButton {
  invalidUnlockCode?: boolean;
}

export function UnlockCodeButton({
  invalidUnlockCode,
}: Readonly<IUnlockCodeButton>): ReactNode {
  const t = useTranslations();
  const { shopConfig } = useShopConfig();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const backgroundColor = shopConfig.commonConfiguration?.sloganTextColor;
  const unlockCode = cookie.get(UNLOCK_CODE_COOKIE);
  const oldUnlockCode = useRef<string | boolean>(unlockCode);

  const drawerTriggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (
      oldUnlockCode.current !== unlockCode &&
      !!unlockCode &&
      !invalidUnlockCode
    ) {
      drawerTriggerRef.current?.click();
    }

    oldUnlockCode.current = unlockCode;
  }, [unlockCode, invalidUnlockCode]);

  return isDesktop ? (
    <Popover>
      <PopoverTrigger>
        <div
          className={cn(buttonVariants({ variant: "outline" }), "w-full")}
          style={{ backgroundColor: backgroundColor ?? "#FDC636" }}
        >
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faUnlock} />
            <div>{t.unlock_code.redeem}</div>
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="w-[400px] p-[16px]"
        style={{
          backgroundColor: backgroundColor ?? "#FDC636",
          border: `1px solid ${backgroundColor ?? "#FDC636"}`,
        }}
      >
        <div className="text-right">
          <Close>
            <X />
          </Close>
        </div>
        <UnlockCodeContent invalidUnlockCode={invalidUnlockCode} />
      </PopoverContent>
    </Popover>
  ) : (
    <Drawer>
      <DrawerTrigger>
        <Button
          type="button"
          variant="outline"
          className="w-full"
          style={{ backgroundColor: backgroundColor ?? "#FDC636" }}
          ref={drawerTriggerRef}
        >
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faUnlock} />
            <div>{t.unlock_code.redeem}</div>
          </div>
        </Button>
      </DrawerTrigger>
      <DrawerContent
        hideBar
        draggable={false}
        className="w-full"
        style={{
          backgroundColor: backgroundColor ?? "#FDC636",
          border: `1px solid ${backgroundColor ?? "#FDC636"}`,
        }}
      >
        <DrawerTitle className="sr-only">{t.unlock_code.title}</DrawerTitle>
        <UnlockCodeContent invalidUnlockCode={invalidUnlockCode} />
      </DrawerContent>
    </Drawer>
  );
}
